import { useRef, useCallback } from 'react';
import { NodeType } from '@/types';
import { useProjectStore } from '@/stores/projectStore';
import { projectApi } from '@/services/projectApi';
import { normalizeAssetUrl } from '@/utils/assetUtils';
import { findClosestAspectRatio } from '@/utils/aspectRatioUtils';

interface UseFileUploadParams {
  nodeId: string;
  nodeType: NodeType;
  onUpdate: (id: string, data: any) => void;
}

export function useFileUpload({ nodeId, nodeType, onUpdate }: UseFileUploadParams) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { currentProjectId } = useProjectStore.getState();

    const processFile = (result: string) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = findClosestAspectRatio(img.width, img.height);
          onUpdate(nodeId, { image: result, videoUri: undefined, audioUri: undefined, aspectRatio, isManualUpload: true, imageDimensions: { width: img.width, height: img.height } });
        };
        img.src = result;
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          const aspectRatio = findClosestAspectRatio(video.videoWidth, video.videoHeight);
          onUpdate(nodeId, { videoUri: result, image: undefined, audioUri: undefined, aspectRatio, isManualUpload: true, videoMetadata: { width: video.videoWidth, height: video.videoHeight, duration: video.duration } });
        };
        video.src = result;
      } else if (file.type.startsWith('audio/')) {
        onUpdate(nodeId, { audioUri: result, image: undefined, videoUri: undefined, isManualUpload: true });
      }
    };

    if (currentProjectId) {
      try {
        const asset = await projectApi.uploadAsset(currentProjectId, file, {
          source: 'uploaded',
          node_id: nodeId,
          node_type: nodeType,
        });
        const assetUrl = normalizeAssetUrl(asset.url);
        processFile(assetUrl);
      } catch (err) {
        console.error('Upload to backend failed, falling back to local:', err);
        const reader = new FileReader();
        reader.onload = (ev) => {
          processFile(ev.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        processFile(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  }, [nodeId, nodeType, onUpdate]);

  const getAcceptTypes = useCallback(() => {
    if (nodeType === NodeType.IMAGE_GENERATOR || nodeType === NodeType.IMAGE_EDITOR) return 'image/*';
    if (nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.VIDEO_COMPOSE) return 'video/*';
    if (nodeType === NodeType.AUDIO_GENERATOR) return 'audio/*';
    return 'image/*,video/*,audio/*';
  }, [nodeType]);

  return {
    fileInputRef,
    handleUploadClick,
    handleFileUpload,
    acceptTypes: getAcceptTypes(),
  };
}
